import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, Plus, UserCheck, RefreshCw, Wand2, CheckCircle2, XCircle, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` });

async function fetchPersonaOverrides() {
  const r = await axios.get(`${API}/api/admin/persona-overrides`, { headers: authHeader() });
  return r.data;
}

async function fetchTopPersonas() {
  const r = await axios.get(`${API}/api/admin/top-personas-dedup`, { headers: authHeader() });
  return r.data;
}

async function savePersonaOverride(persona_raw, persona_norm) {
  const r = await axios.post(`${API}/api/admin/persona-overrides`, { persona_raw, persona_norm }, { headers: authHeader() });
  return r.data;
}

async function deletePersonaOverride(persona_raw) {
  await axios.delete(`${API}/api/admin/persona-overrides/${encodeURIComponent(persona_raw)}`, { headers: authHeader() });
}

async function saveActividadOverride(actividad_raw, actividad_norm) {
  const r = await axios.post(`${API}/api/admin/actividad-overrides`, { actividad_raw, actividad_norm }, { headers: authHeader() });
  return r.data;
}

async function deleteActividadOverride(actividad_raw) {
  await axios.delete(`${API}/api/admin/actividad-overrides/${encodeURIComponent(actividad_raw)}`, { headers: authHeader() });
}

async function fetchActividadOverrides() {
  const r = await axios.get(`${API}/api/admin/actividad-overrides`, { headers: authHeader() });
  return r.data;
}

export function MapeoPersonasTab() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('personas'); // 'personas' | 'actividades' | 'auditoria'
  const [searchP, setSearchP] = useState('');
  const [searchA, setSearchA] = useState('');
  const [newRaw, setNewRaw]   = useState('');
  const [newNorm, setNewNorm] = useState('');
  const [newActRaw, setNewActRaw]   = useState('');
  const [newActNorm, setNewActNorm] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Reapply overrides state ───────────────────────────────────────────────
  const [reapplying, setReapplying] = useState(false);
  const [reapplyResult, setReapplyResult] = useState(null);

  const reapplyAllOverrides = async () => {
    setReapplying(true);
    setReapplyResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/reapply-all-overrides`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` }
      });
      setReapplyResult(r.data);
      toast.success(`Re-aplicación completada: ${r.data.total_updated} registros actualizados`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al re-aplicar overrides');
    } finally { setReapplying(false); }
  };

  // ── Auditoría IA state ───────────────────────────────────────────────────
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditResults, setAuditResults] = useState(null); // { suggestions, total_names, candidate_groups }
  const [dismissed, setDismissed] = useState(new Set());
  const [approved, setApproved] = useState(new Set());
  const [approvingItem, setApprovingItem] = useState(null);

  const { data: personaOvs = [], isLoading: pLoading } = useQuery({
    queryKey: ['persona-overrides'],
    queryFn: fetchPersonaOverrides,
  });

  const { data: actOvs = [], isLoading: aLoading } = useQuery({
    queryKey: ['actividad-overrides'],
    queryFn: fetchActividadOverrides,
  });

  const filteredP = personaOvs.filter(o =>
    !searchP || o.persona_raw?.toLowerCase().includes(searchP.toLowerCase()) ||
    o.persona_norm?.toLowerCase().includes(searchP.toLowerCase())
  );

  const filteredA = actOvs.filter(o =>
    !searchA || o.actividad_raw?.toLowerCase().includes(searchA.toLowerCase()) ||
    o.actividad_norm?.toLowerCase().includes(searchA.toLowerCase())
  );

  const handleAddPersona = async () => {
    if (!newRaw.trim() || !newNorm.trim()) { toast.error('Rellena ambos campos'); return; }
    setSaving(true);
    try {
      const res = await savePersonaOverride(newRaw.trim(), newNorm.trim());
      toast.success(`Guardado. ${res.updated_records} registros actualizados.`);
      setNewRaw(''); setNewNorm('');
      qc.invalidateQueries({ queryKey: ['persona-overrides'] });
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDeletePersona = async (raw) => {
    try {
      await deletePersonaOverride(raw);
      toast.success('Override eliminado');
      qc.invalidateQueries({ queryKey: ['persona-overrides'] });
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddActividad = async () => {
    if (!newActRaw.trim() || !newActNorm.trim()) { toast.error('Rellena ambos campos'); return; }
    setSaving(true);
    try {
      const res = await saveActividadOverride(newActRaw.trim(), newActNorm.trim());
      toast.success(`Guardado. ${res.updated_records} registros actualizados.`);
      setNewActRaw(''); setNewActNorm('');
      qc.invalidateQueries({ queryKey: ['actividad-overrides'] });
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDeleteActividad = async (raw) => {
    try {
      await deleteActividadOverride(raw);
      toast.success('Override eliminado');
      qc.invalidateQueries({ queryKey: ['actividad-overrides'] });
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Auditoría IA handlers ────────────────────────────────────────────────
  const runAudit = async () => {
    setAuditRunning(true);
    setAuditResults(null);
    setDismissed(new Set());
    setApproved(new Set());
    try {
      const r = await axios.post(`${API}/api/admin/persona-ai-audit`, {}, { headers: authHeader(), timeout: 180000 });
      setAuditResults(r.data);
      toast.success(`Auditoría completada: ${r.data.suggestions_count} sugerencias encontradas${r.data.ai_available === false ? ' (modo automático — IA no disponible)' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al ejecutar la auditoría IA');
    } finally {
      setAuditRunning(false);
    }
  };

  const approveItem = async (item) => {
    setApprovingItem(item.wrong);
    try {
      const res = await axios.post(`${API}/api/admin/persona-overrides`,
        { persona_raw: item.wrong, persona_norm: item.canonical },
        { headers: authHeader() }
      );
      setApproved(prev => new Set([...prev, item.wrong]));
      toast.success(`Aprobado. ${res.data.updated_records ?? 0} registros actualizados`);
      qc.invalidateQueries({ queryKey: ['persona-overrides'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aprobar');
    } finally {
      setApprovingItem(null);
    }
  };

  const dismissItem = (wrong) => {
    setDismissed(prev => new Set([...prev, wrong]));
  };

  const approveAll = async () => {
    const pending = (auditResults?.suggestions || []).filter(
      s => !approved.has(s.wrong) && !dismissed.has(s.wrong)
    );
    for (const item of pending) {
      await approveItem(item);
    }
  };

  const TAB_STYLE = (active) => ({
    padding: '5px 14px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    cursor: 'pointer',
    background: active ? 'rgba(74,158,255,0.18)' : 'transparent',
    border: `1px solid ${active ? 'rgba(74,158,255,0.5)' : 'rgba(74,158,255,0.1)'}`,
    color: active ? '#4A9EFF' : 'var(--vz-text-secondary)',
    transition: 'all .15s',
  });

  return (
    <div className="space-y-5" data-testid="mapeo-personas-tab">
      <div>
        <h3 className="font-montserrat font-bold text-sm text-white mb-0.5">Corrección de nombres</h3>
        <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
          Unifica variantes de nombres de personas y tipos de actividad procedentes del formulario ArcGIS.
          Los cambios se aplican inmediatamente a los datos en caché.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap">
        <button style={TAB_STYLE(tab === 'personas')} onClick={() => setTab('personas')}>
          <span className="flex items-center gap-1.5"><UserCheck size={11} /> Personas ({personaOvs.length})</span>
        </button>
        <button style={TAB_STYLE(tab === 'actividades')} onClick={() => setTab('actividades')}>
          <span className="flex items-center gap-1.5"><RefreshCw size={11} /> Actividades ({actOvs.length})</span>
        </button>
        <button style={TAB_STYLE(tab === 'auditoria')} onClick={() => setTab('auditoria')} data-testid="tab-auditoria-ia">
          <span className="flex items-center gap-1.5"><Sparkles size={11} /> Auditoría IA</span>
        </button>
      </div>

      {/* ── PERSONAS ── */}
      {tab === 'personas' && (
        <div className="space-y-4">
          {/* Re-apply overrides banner */}
          <div className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.18)' }}>
            <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
              Tras aprobar correcciones, pulsa <strong style={{ color: '#FFC107' }}>Re-aplicar a BD</strong> para actualizar todos los registros históricos.
            </p>
            <Button size="sm" className="h-7 text-xs gap-1.5 shrink-0 ml-3" disabled={reapplying}
              style={{ background: 'rgba(255,193,7,0.15)', border: '1px solid rgba(255,193,7,0.35)', color: '#FFC107' }}
              onClick={reapplyAllOverrides} data-testid="btn-reapply-personas">
              {reapplying ? <><Loader2 size={10} className="animate-spin" /> Aplicando...</> : <><RefreshCw size={10} /> Re-aplicar a BD</>}
            </Button>
          </div>

          {reapplyResult && (
            <div className="p-3 rounded-lg text-xs space-y-1.5" style={{ background: 'rgba(76,175,80,0.06)', border: '1px solid rgba(76,175,80,0.2)' }} data-testid="reapply-personas-result">
              <p className="font-semibold" style={{ color: '#4CAF50' }}>
                {reapplyResult.total_updated} registros actualizados en BD
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(reapplyResult.steps || {}).map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded font-mono" style={{ background: 'rgba(0,0,0,0.2)', color: v?.error ? '#FF5252' : '#4CAF50' }}>
                    {k.replace(/_/g,' ')}: {v?.error ? 'ERROR' : `+${v?.updated ?? 0}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add new */}
          <div className="rounded-xl border p-4 space-y-3" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
            <p className="text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>Añadir corrección de persona</p>
            <div className="flex gap-2 flex-wrap">
              <Input
                placeholder="Nombre incorrecto (ej: Juan Pedro Sanchez)"
                value={newRaw}
                onChange={e => setNewRaw(e.target.value)}
                className="text-xs h-8 flex-1 min-w-[200px]"
                style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
              />
              <Input
                placeholder="Nombre correcto (ej: Juan Pedro Sanchez Ramirez)"
                value={newNorm}
                onChange={e => setNewNorm(e.target.value)}
                className="text-xs h-8 flex-1 min-w-[200px]"
                style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
              />
              <Button size="sm" onClick={handleAddPersona} disabled={saving} className="h-8 text-xs gap-1" style={{ background: '#4A9EFF', color: '#fff' }}>
                <Plus size={12} /> Guardar
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
            <Input
              placeholder="Buscar override..."
              value={searchP}
              onChange={e => setSearchP(e.target.value)}
              className="pl-8 h-8 text-xs"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
            />
          </div>

          {/* Table */}
          {pLoading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
          ) : filteredP.length === 0 ? (
            <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
              {personaOvs.length === 0 ? 'Sin correcciones registradas. Añade la primera arriba.' : 'Sin resultados para la búsqueda.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  {['Nombre en ArcGIS (incorrecto)', 'Nombre normalizado (correcto)', 'Acción'].map(h => (
                    <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredP.map((ov, i) => (
                  <TableRow key={i} className="hover:bg-[#132860] transition-colors" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    <TableCell className="text-xs py-2 font-mono" style={{ color: '#FF7043' }}>{ov.persona_raw}</TableCell>
                    <TableCell className="text-xs py-2 font-medium" style={{ color: 'var(--vz-text-primary)' }}>{ov.persona_norm}</TableCell>
                    <TableCell className="text-xs py-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePersona(ov.persona_raw)}
                        className="h-7 w-7 p-0 hover:bg-red-500/10"
                        title="Eliminar"
                        data-testid={`delete-persona-override-${i}`}
                      >
                        <Trash2 size={12} style={{ color: '#FF5252' }} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* ── ACTIVIDADES ── */}
      {tab === 'actividades' && (
        <div className="space-y-4">
          {/* Add new */}
          <div className="rounded-xl border p-4 space-y-3" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
            <p className="text-xs font-semibold" style={{ color: 'var(--vz-text-secondary)' }}>Añadir corrección de actividad</p>
            <div className="flex gap-2 flex-wrap">
              <Input
                placeholder="Valor incorrecto (ej: Charlamotivacional)"
                value={newActRaw}
                onChange={e => setNewActRaw(e.target.value)}
                className="text-xs h-8 flex-1 min-w-[200px]"
                style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
              />
              <Input
                placeholder="Valor correcto (ej: Charla motivacional)"
                value={newActNorm}
                onChange={e => setNewActNorm(e.target.value)}
                className="text-xs h-8 flex-1 min-w-[200px]"
                style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
              />
              <Button size="sm" onClick={handleAddActividad} disabled={saving} className="h-8 text-xs gap-1" style={{ background: '#4A9EFF', color: '#fff' }}>
                <Plus size={12} /> Guardar
              </Button>
            </div>
          </div>

          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
            <Input
              placeholder="Buscar override..."
              value={searchA}
              onChange={e => setSearchA(e.target.value)}
              className="pl-8 h-8 text-xs"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-primary)' }}
            />
          </div>

          {aLoading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
          ) : filteredA.length === 0 ? (
            <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
              {actOvs.length === 0 ? 'Sin correcciones registradas.' : 'Sin resultados.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  {['Valor en ArcGIS', 'Valor normalizado', 'Acción'].map(h => (
                    <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)' }}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredA.map((ov, i) => (
                  <TableRow key={i} className="hover:bg-[#132860] transition-colors" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    <TableCell className="text-xs py-2 font-mono" style={{ color: '#FF7043' }}>{ov.actividad_raw}</TableCell>
                    <TableCell className="text-xs py-2 font-medium" style={{ color: 'var(--vz-text-primary)' }}>{ov.actividad_norm}</TableCell>
                    <TableCell className="text-xs py-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteActividad(ov.actividad_raw)}
                        className="h-7 w-7 p-0 hover:bg-red-500/10"
                        title="Eliminar"
                        data-testid={`delete-actividad-override-${i}`}
                      >
                        <Trash2 size={12} style={{ color: '#FF5252' }} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* ── AUDITORÍA IA ── */}
      {tab === 'auditoria' && (
        <div className="space-y-4" data-testid="auditoria-ia-panel">
          {/* Header card */}
          <div className="p-4 rounded-xl border space-y-3" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'rgba(74,158,255,0.2)' }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white flex items-center gap-2">
                  <Sparkles size={14} style={{ color: '#4A9EFF' }} /> Auditoría de nombres con IA
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>
                  Claude analiza todos los nombres únicos de la base de datos, detecta typos y variantes erróneas,
                  y sugiere correcciones. Puedes aprobar o ignorar cada sugerencia individualmente.
                </p>
              </div>
              <Button
                size="sm"
                className="h-8 text-xs gap-1.5 shrink-0"
                style={{ background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: '#4A9EFF' }}
                onClick={runAudit}
                disabled={auditRunning}
                data-testid="btn-run-audit"
              >
                {auditRunning
                  ? <><Loader2 size={12} className="animate-spin" /> Analizando...</>
                  : <><Sparkles size={12} /> Iniciar auditoría</>}
              </Button>
            </div>

            {auditResults && (
              <div className="flex gap-4 text-xs pt-1 border-t" style={{ borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-muted)' }}>
                <span><strong style={{ color: '#4A9EFF' }}>{auditResults.total_names}</strong> nombres únicos analizados</span>
                <span><strong style={{ color: '#4A9EFF' }}>{auditResults.digit_suspects ?? 0}</strong> sospechosos con dígito</span>
                <span><strong style={{ color: '#4A9EFF' }}>{auditResults.candidate_groups}</strong> grupos candidatos</span>
                <span><strong style={{ color: '#FFD600' }}>{auditResults.suggestions_count}</strong> sugerencias nuevas</span>
                {auditResults.ai_available === false && (
                  <span style={{ color: '#FF9800', fontStyle: 'italic' }}>⚠ Modo automático (Claude no disponible)</span>
                )}
                <span><strong style={{ color: '#4CAF50' }}>{approved.size}</strong> aprobadas</span>
                <span><strong style={{ color: 'var(--vz-text-muted)' }}>{dismissed.size}</strong> ignoradas</span>
              </div>
            )}
          </div>

          {/* Loading state */}
          {auditRunning && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" style={{ color: 'var(--vz-text-muted)' }}>
              <Loader2 size={28} className="animate-spin" style={{ color: '#4A9EFF' }} />
              <p className="text-sm">Claude está analizando los nombres... (puede tardar 30-60 segundos)</p>
            </div>
          )}

          {/* Results */}
          {!auditRunning && auditResults && (() => {
            const pending = auditResults.suggestions.filter(s => !approved.has(s.wrong) && !dismissed.has(s.wrong));
            const approvedList = auditResults.suggestions.filter(s => approved.has(s.wrong));
            return (
              <div className="space-y-3">
                {pending.length > 0 && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
                      {pending.length} sugerencias pendientes de revisión
                    </p>
                    <Button
                      size="sm"
                      className="h-7 text-xs gap-1"
                      style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', color: '#4CAF50' }}
                      onClick={approveAll}
                      data-testid="btn-approve-all"
                    >
                      <CheckCircle2 size={11} /> Aprobar todas ({pending.length})
                    </Button>
                  </div>
                )}

                {pending.length === 0 && approvedList.length === 0 && (
                  <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
                    No se encontraron sugerencias nuevas. La base de datos está limpia.
                  </p>
                )}

                {pending.length === 0 && approvedList.length > 0 && (
                  <p className="text-xs py-4 text-center" style={{ color: '#4CAF50' }}>
                    Todas las sugerencias han sido procesadas.
                  </p>
                )}

                {pending.length > 0 && (
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
                    <Table>
                      <TableHeader>
                        <TableRow style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-elevated)' }}>
                          <TableHead className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vz-text-muted)' }}>Nombre incorrecto (ArcGIS)</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vz-text-muted)' }}>Corrección sugerida</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>Motivo</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vz-text-muted)' }}>Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pending.map((item, i) => (
                          <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                            <TableCell className="text-xs py-2 font-mono" style={{ color: '#FF7043' }}>{item.wrong}</TableCell>
                            <TableCell className="text-xs py-2 font-medium" style={{ color: '#4CAF50' }}>{item.canonical}</TableCell>
                            <TableCell className="text-xs py-2 hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)', maxWidth: 200 }}>
                              {item.reason}
                            </TableCell>
                            <TableCell className="text-xs py-2">
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-[10px] gap-1"
                                  style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', color: '#4CAF50' }}
                                  onClick={() => approveItem(item)}
                                  disabled={approvingItem === item.wrong}
                                  data-testid={`approve-suggestion-${i}`}
                                >
                                  {approvingItem === item.wrong
                                    ? <Loader2 size={9} className="animate-spin" />
                                    : <CheckCircle2 size={10} />}
                                  Aprobar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 px-2 text-[10px] gap-1"
                                  style={{ color: 'var(--vz-text-muted)' }}
                                  onClick={() => dismissItem(item.wrong)}
                                  data-testid={`dismiss-suggestion-${i}`}
                                >
                                  <XCircle size={10} /> Ignorar
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Empty state before first run */}
          {!auditRunning && !auditResults && (
            <div className="flex flex-col items-center justify-center py-16 gap-2" style={{ color: 'var(--vz-text-muted)' }}>
              <Sparkles size={28} style={{ color: 'rgba(74,158,255,0.4)' }} />
              <p className="text-sm">Pulsa "Iniciar auditoría" para que Claude analice todos los nombres de la BD</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
